import showMobileFilters from "@NA/Handlers/ADIShowMobileAttributeFilterHandler";
import { CategoryDetailsPageContext } from "@NA/Overrides/Pages/CategoryDetailsPage";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    CategoryFacetModel,
    FacetModel,
    PriceFacetModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ProductListPageContext, ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import { formatPriceRangeFacet } from "@insite/content-library/Widgets/ProductList/ProductListPriceFilters";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency, language },
        },
    } = state;
    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;

        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;
        const mergeAttributeTypeFacets = (
            unfilteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
            filteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
        ) => {
            if (!unfilteredAttributeTypeFacets) {
                return filteredAttributeTypeFacets;
            }

            if (!filteredAttributeTypeFacets) {
                return unfilteredAttributeTypeFacets;
            }

            return unfilteredAttributeTypeFacets.concat(
                filteredAttributeTypeFacets.filter(
                    o => !unfilteredAttributeTypeFacets.some(p => p.attributeTypeId === o.attributeTypeId),
                ),
            );
        };

        return {
            loaded: true,
            stockedItemsOnly: productFilters.stockedItemsOnly,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: mergeAttributeTypeFacets(
                unfilteredProductCollection?.attributeTypeFacets,
                filteredProductCollection?.attributeTypeFacets,
            ),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributes,
            selectedCategoryId: productFilters.categoryId,
            currency,
            language,
            showMobileFiltersState: state.ADIGlobalData.ADIMobileFilterStore.showMobileFilters as any,
            myBranchDetails: state.ADIGlobalData.myBranchStore as any,
        };
    }
    return {
        loaded: false,
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        brandFacets: [] as FacetModel[],
        productLineFacets: [] as FacetModel[],
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        attributeTypeFacets: [] as AttributeTypeFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        language: null,
        showMobileFiltersState: null,
        myBranchDetails: null,
    };
};

const mapDispatchToProps = {
    removeProductFilters,
    clearAllProductFilters,
    showMobileFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;
//type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListFilterListStyles {
    wrapper?: InjectableCss;
    skipFiltersButtonWrapper?: InjectableCss;
    skipFiltersButton?: SkipNavStyles;
    filterText?: TypographyPresentationProps;
    filterTag?: TagPresentationProps;
    clearAllLink?: LinkPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFiltersText?: InjectableCss;
    allTagsContainer?: InjectableCss;
    mobileExpandContainer?: InjectableCss;
    mobileClearAll?: InjectableCss;
    mobileContainer?: InjectableCss;
    mobileFilters?: InjectableCss;
    toggleMsg?: TypographyProps;
}

export const filterListStyles: ProductListFilterListStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    filterText: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
            font-size: ${getProp("theme.typography.h6.size")};
            font-weight: 800;
        `,
    },
    skipFiltersButtonWrapper: {
        css: css`
            position: relative;
            top: -65px;
            width: 100%;
        `,
    },
    clearAllLink: {
        css: css`
            padding-bottom: 0px;
        `,
    },
    appliedFiltersContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            border-top: 1px solid ${getColor("text.disabled")};
            padding: 10px 0;
        `,
    },
    appliedFiltersText: {
        css: css`
            font-weight: bold;
        `,
    },
    allTagsContainer: {
        css: css`
            width: 100%;
            background-color: ${getColor("text.disabled")};
            border-radius: 4px;
            display: flex;
            flex-wrap: wrap;
            padding: 10px;
        `,
    },
    filterTag: {
        css: css`
            background-color: transparent;
            flex-direction: row-reverse;
            padding: 0;
            margin: 0 !important;
            min-height: 24px !important;
            span {
                font-size: 12px !important;
                text-decoration: underline;
                margin-left: 3px;
                margin-top: 3px;
                color: #000;
            }
            button {
                margin-left: 0;
                height: auto;
                flex-basis: auto;
                background: transparent;
                min-height: initial;

                :hover {
                    background-color: transparent;
                }
                span {
                    margin-left: 0;
                    width: auto;
                    height: auto;
                    top: 0px !important;
                    svg {
                        width: 13px;
                        height: 15px;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: row;
                            border-radius: 4px;
                            background-color: ${getColor("text.disabled")};
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            font-weight: bold;
                            line-height: normal;
                            color: #666 !important;
                            padding: 5.5px 10px 3.5px;
                            margin: 0;
                            display: inline-flex;
                            align-items: center;
                            margin: 12px 9px 0 0;
                            span {
                                text-decoration: none;
                            }
                            button {
                                span {
                                    top: 1px !important;
                                }
                            }
                        `,
                        css`
                            flex-direction: row;
                            border-radius: 4px;
                            background-color: ${getColor("text.disabled")};
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            font-weight: bold;
                            line-height: normal;
                            color: #666 !important;
                            padding: 5.5px 10px 3.5px;
                            margin: 0;
                            display: inline-flex;
                            align-items: center;
                            margin: 12px 9px 0 0;
                            span {
                                text-decoration: none;
                            }
                            button {
                                span {
                                    top: 1px !important;
                                }
                            }
                        `,
                        css`
                            flex-direction: row;
                            border-radius: 4px;
                            background-color: ${getColor("text.disabled")};
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            font-weight: bold;
                            line-height: normal;
                            color: #666 !important;
                            padding: 5.5px 9px 3.5px;
                            margin: 0;
                            display: inline-flex;
                            align-items: center;
                            margin: 12px 10px 0 0;
                            span {
                                text-decoration: none;
                            }
                            button {
                                span {
                                    top: 1px !important;
                                }
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    mobileExpandContainer: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-between;
            border-radius: 4px;
            background-color: #f7f7f7;
            padding: 19px 15px 18px 15px;
            height: 59px;
            span {
                font-size: ${getProp("theme.typography.h6.size")};
                font-weight: 800;
                line-height: normal;
                color: #002856;
            }
        `,
    },
    mobileClearAll: {
        css: css`
            button {
                border-radius: 4px;
                border: solid 1px ${getColor("secondary")};
                background-color: #fff;
                font-size: ${getProp("theme.typography.headerSecondary.size")};
                font-weight: bold;
                line-height: normal !important;
                color: ${getColor("secondary")};
                padding: 5.5px 20px 3.5px !important;
                width: auto;
                height: auto;
                min-height: 34px;
                margin: 12px 8px 0 0;
            }
        `,
    },
    mobileContainer: {
        css: css`
            padding: 0 10px;
            display: flex;
            margin-bottom: 12px;
            width: 100%;
            flex-wrap: wrap;
        `,
    },
    mobileFilters: {
        css: css`
            width: 100%;
        `,
    },
    toggleMsg: {
        css: css`
            font-size: 16px !important;
            font-weight: normal !important;
            line-height: normal !important;
            letter-spacing: normal !important;
            color: ${getColor("primary")} !important;
        `,
    },
};

const styles = filterListStyles;

const ProductListFilterList: FC<Props> = ({
    loaded,
    stockedItemsOnly,
    previouslyPurchasedProducts,
    brandFacets,
    productLineFacets,
    priceFacets,
    categoryFacets,
    attributeTypeFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    removeProductFilters,
    clearAllProductFilters,
    currency,
    id,
    language,
    showMobileFilters,
    showMobileFiltersState,
    myBranchDetails,
}) => {
    const [toggle, setToggleState] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState<boolean>(false);

    React.useEffect(() => {
        const onWindowResize = () => {
            if (window.innerWidth >= 900) {
                setIsMobile(false);
            } else {
                setIsMobile(true);
            }
        };
        onWindowResize();

        if (typeof window !== "undefined") {
            window.addEventListener("resize", onWindowResize);
        }

        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", onWindowResize);
            }
        };
    }, []);

    React.useEffect(() => {
        showMobileFilters({
            showMobileFilters: isMobile,
        });
    }, [isMobile, attributeTypeFacets, showMobileFiltersState]);

    if (!loaded) {
        return null;
    }

    const updateUrlWithoutBoostIds = (attributeId: string) => {
        const url = new URL(window.location.href);
        const currentAttributes = url.searchParams.get("attributes");
        if (currentAttributes) {
            const updatedAttributes = currentAttributes
                .split(',')
                .filter(attr => attr !== attributeId)
                .join(',');

            if (updatedAttributes) {
                url.searchParams.set("attributes", updatedAttributes);
            } else {
                url.searchParams.delete("attributes");
            }
        }
        window.history.replaceState({}, '', url.toString());
        window.location.reload();
    };

    const handleClearAllProductFilters = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete("attributes");
        url.searchParams.delete("boostids");
        window.history.replaceState({}, '', url.toString());
        window.location.reload();
    };

    const clickRemoveStockedItemsOnly = () => {
        removeProductFilters({ stockedItemsOnly: false });
    };

    const clickRemovePreviouslyPurchasedProducts = () => {
        removeProductFilters({ previouslyPurchasedProducts: false });
    };

    const clickRemoveSearchHandler = (search: string) => {
        removeProductFilters({ searchWithinQueries: [search] });
    };

    const clickRemoveBrandHandler = (id: string) => {
        removeProductFilters({ brandIds: [id] });
    };

    const clickRemoveProductLineHandler = (id: string) => {
        removeProductFilters({ productLineIds: [id] });
    };

    const clickRemovePriceHandler = (price: string) => {
        removeProductFilters({ priceFilters: [price] });
    };

    const clickRemoveAttributeValueHandler = (id: string) => {
        const url = new URL(window.location.href);
        if (url.searchParams.has("boostids")) {
            removeProductFilters({ attributes: [id] });
            updateUrlWithoutBoostIds(id);
        } else {
            removeProductFilters({ attributes: [id] });
        }
    };

    const clickRemoveCategoryHandler = () => {
        removeProductFilters({ categoryId: selectedCategoryId });
    };

    const anyActiveFilters =
        searchWithinQueries?.length ||
        stockedItemsOnly ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        selectedCategoryId ||
        previouslyPurchasedProducts ||
        stockedItemsOnly ||
        (searchWithinQueries && searchWithinQueries?.length);

    return (
        <StyledWrapper {...styles.wrapper}>
            {showMobileFiltersState ? (
                <>
                    <StyledWrapper
                        {...styles.mobileExpandContainer}
                        onClick={() => {
                            setToggleState(!toggle);
                        }}
                    >
                        <Typography>
                            {translate("Filter Results")}{" "}
                            {selectedAttributeValuesIds?.length ? `(${selectedAttributeValuesIds?.length})` : null}
                        </Typography>
                        <Typography {...styles.toggleMsg}>
                            {!toggle ? translate("+ Expand") : translate("- Collapse")}
                        </Typography>
                    </StyledWrapper>
                    {toggle && (
                        <>
                            <StyledWrapper {...styles.mobileContainer}>
                                {anyActiveFilters && (
                                    <StyledWrapper {...styles.mobileClearAll}>
                                        <Link
                                            {...styles.clearAllLink}
                                            onClick={() => {
                                                const url = new URL(window.location.href);
                                                if (url.searchParams.has("boostids")) {
                                                    handleClearAllProductFilters();
                                                } else {
                                                    clearAllProductFilters();
                                                }
                                            }
                                                }
                                            data-test-selector="productListClearAll"
                                        >
                                            {translate("Clear All")}
                                        </Link>
                                    </StyledWrapper>
                                )}

                                {previouslyPurchasedProducts && (
                                    <Tag
                                        {...styles.filterTag}
                                        onDelete={clickRemovePreviouslyPurchasedProducts}
                                        data-test-selector="previouslyPurchasedProductsFilter"
                                    >
                                        {translate("Previously Purchased Only")}
                                    </Tag>
                                )}
                                {stockedItemsOnly && (
                                    <Tag
                                        {...styles.filterTag}
                                        onDelete={clickRemoveStockedItemsOnly}
                                        data-test-selector="stockedItemsOnlyFilter"
                                    >
                                        {translate("Stocked Items Only")}
                                    </Tag>
                                )}
                                {searchWithinQueries?.map(s => (
                                    <Tag
                                        key={s}
                                        {...styles.filterTag}
                                        onDelete={() => clickRemoveSearchHandler(s)}
                                        data-test-selector="searchWithinFilter"
                                    >
                                        {s}
                                    </Tag>
                                ))}
                                {brandFacets &&
                                    selectedBrandIds?.map(id => (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemoveBrandHandler(id)}
                                            data-test-selector={`brandFilter${id}`}
                                        >
                                            {brandFacets?.find(o => o.id === id)?.name}
                                        </Tag>
                                    ))}
                                {productLineFacets &&
                                    selectedProductLineIds?.map(id => (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemoveProductLineHandler(id)}
                                            data-test-selector={`productLineFilter${id}`}
                                        >
                                            {productLineFacets?.find(o => o.id === id)?.name}
                                        </Tag>
                                    ))}
                                {selectedCategoryId && (
                                    <Tag
                                        {...styles.filterTag}
                                        onDelete={() => clickRemoveCategoryHandler()}
                                        data-test-selector={`categoryFilter${selectedCategoryId}`}
                                    >
                                        {
                                            categoryFacets?.find(o => o.categoryId === selectedCategoryId)
                                                ?.shortDescription
                                        }
                                    </Tag>
                                )}
                                {priceFacets &&
                                    selectedPriceFilters?.map(id => (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemovePriceHandler(id)}
                                            data-test-selector={`priceFilter${id}`}
                                        >
                                            {formatPriceRangeFacet(
                                                priceFacets?.find(o => o.minimumPrice.toString() === id),
                                                currency,
                                            )}
                                        </Tag>
                                    ))}
                                {attributeTypeFacets &&
                                    selectedAttributeValuesIds?.map(id => {
                                        const attributeTypeFacet = attributeTypeFacets?.find(at =>
                                            at.attributeValueFacets?.find(av => av.attributeValueId === id),
                                        );
                                        const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                                            av => av.attributeValueId === id,
                                        );

                                        return (
                                            <Tag
                                                key={id}
                                                {...styles.filterTag}
                                                onDelete={() => clickRemoveAttributeValueHandler(id)}
                                                data-test-selector={`attributeValueFilter${id}`}
                                            >
                                                {attributeValueFacet?.valueDisplay.includes("~")
                                                    ? attributeValueFacet?.valueDisplay.split("~")[1]
                                                    : myBranchDetails
                                                    ? attributeValueFacet?.valueDisplay ===
                                                      myBranchDetails?.warehouseCode
                                                        ? `${myBranchDetails?.warehouseNameSection} ${translate(
                                                              "Branch",
                                                          )}`
                                                        : attributeValueFacet?.valueDisplay
                                                    : attributeValueFacet?.valueDisplay}
                                            </Tag>
                                        );
                                    })}
                            </StyledWrapper>
                            {isMobile ? (
                                <StyledWrapper {...styles.mobileFilters}>
                                    <Zone contentId={id} zoneName="mobileFilter"></Zone>
                                </StyledWrapper>
                            ) : null}
                        </>
                    )}
                </>
            ) : (
                <>
                    <Typography {...styles.filterText} data-test-selector="productListFilterList">
                        {translate("Filter Results")}
                    </Typography>
                    <StyledWrapper style={{ width: "100%" }}>
                        {anyActiveFilters && (
                            <StyledWrapper {...styles.appliedFiltersContainer}>
                                <Typography {...styles.appliedFiltersText}>{translate("Applied Filters")}</Typography>
                                <Link
                                    {...styles.clearAllLink}
                                        onClick={() => {
                                            const url = new URL(window.location.href);
                                            if (url.searchParams.has("boostids")) {
                                                handleClearAllProductFilters();
                                            } else {
                                                clearAllProductFilters();
                                            }
                                        }
                                        }
                                    data-test-selector="productListClearAll"
                                >
                                    {translate("Clear All")}
                                </Link>
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                            <ProductListPageDataContext.Consumer>
                                {({ ref }) => {
                                    return ref ? (
                                        <SkipNav
                                            text={translate("Skip to Results")}
                                            extendedStyles={styles.skipFiltersButton}
                                            destination={ref}
                                        />
                                    ) : undefined;
                                }}
                            </ProductListPageDataContext.Consumer>
                        </StyledWrapper>
                        {anyActiveFilters &&
                            <StyledWrapper {...styles.allTagsContainer}>
                                {previouslyPurchasedProducts && (
                                    <Tag
                                        {...styles.filterTag}
                                        onDelete={clickRemovePreviouslyPurchasedProducts}
                                        data-test-selector="previouslyPurchasedProductsFilter"
                                    >
                                        {translate("Previously Purchased Only")}
                                    </Tag>
                                )}
                                {stockedItemsOnly && (
                                    <Tag
                                        {...styles.filterTag}
                                        onDelete={clickRemoveStockedItemsOnly}
                                        data-test-selector="stockedItemsOnlyFilter"
                                    >
                                        {translate("Stocked Items Only")}
                                    </Tag>
                                )}
                                {searchWithinQueries?.map(s => (
                                    <Tag
                                        key={s}
                                        {...styles.filterTag}
                                        onDelete={() => clickRemoveSearchHandler(s)}
                                        data-test-selector="searchWithinFilter"
                                    >
                                        {s}
                                    </Tag>
                                ))}
                                {brandFacets &&
                                    selectedBrandIds?.map(id => (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemoveBrandHandler(id)}
                                            data-test-selector={`brandFilter${id}`}
                                        >
                                            {brandFacets?.find(o => o.id === id)?.name}
                                        </Tag>
                                    ))}
                                {productLineFacets &&
                                    selectedProductLineIds?.map(id => (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemoveProductLineHandler(id)}
                                            data-test-selector={`productLineFilter${id}`}
                                        >
                                            {productLineFacets?.find(o => o.id === id)?.name}
                                        </Tag>
                                    ))}
                                {selectedCategoryId && (
                                    <Tag
                                        {...styles.filterTag}
                                        onDelete={() => clickRemoveCategoryHandler()}
                                        data-test-selector={`categoryFilter${selectedCategoryId}`}
                                    >
                                        {categoryFacets?.find(o => o.categoryId === selectedCategoryId)?.shortDescription}
                                    </Tag>
                                )}
                                {priceFacets &&
                                    selectedPriceFilters?.map(id => (
                                        <Tag
                                            key={id}
                                            {...styles.filterTag}
                                            onDelete={() => clickRemovePriceHandler(id)}
                                            data-test-selector={`priceFilter${id}`}
                                        >
                                            {formatPriceRangeFacet(
                                                priceFacets?.find(o => o.minimumPrice.toString() === id),
                                                currency,
                                            )}
                                        </Tag>
                                    ))}
                                {attributeTypeFacets &&
                                    selectedAttributeValuesIds?.map(id => {
                                        const attributeTypeFacet = attributeTypeFacets?.find(at =>
                                            at.attributeValueFacets?.find(av => av.attributeValueId === id),
                                        );
                                        const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                                            av => av.attributeValueId === id,
                                        );
                                        if (attributeValueFacet && attributeValueFacet.valueDisplay) {
                                            return (
                                                <Tag
                                                    key={id}
                                                    {...styles.filterTag}
                                                    onDelete={() => clickRemoveAttributeValueHandler(id)}
                                                    data-test-selector={`attributeValueFilter${id}`}
                                                >
                                                    {attributeValueFacet?.valueDisplay.includes("~")
                                                        ? attributeValueFacet?.valueDisplay.split("~")[1]
                                                        : myBranchDetails
                                                        ? attributeValueFacet?.valueDisplay ===
                                                        myBranchDetails.warehouseCode
                                                            ? `${myBranchDetails.warehouseNameSection} ${translate(
                                                                "Branch",
                                                            )}`
                                                            : attributeValueFacet?.valueDisplay
                                                        : attributeValueFacet?.valueDisplay}
                                                </Tag>
                                            );
                                        } else {
                                            return "";
                                        }
                                    })}
                            </StyledWrapper>
                        }
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListFilterList),
    definition: {
        group: "Product List",
        displayName: "Filter List",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
